import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useNetworkStatus, type UseNetworkStatusReturn } from '@/hooks/useNetworkStatus';
import { useQueryClient } from '@tanstack/react-query';

/**
 * ============================================
 * Network Context
 * ============================================
 *
 * Provides global network status across the app.
 * On reconnect, invalidates all React Query caches.
 */

const NetworkContext = createContext<UseNetworkStatusReturn | null>(null);

export function useNetwork(): UseNetworkStatusReturn {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within NetworkProvider');
  }
  return context;
}

export interface NetworkProviderProps {
  children: ReactNode;
}

export function NetworkProvider({ children }: NetworkProviderProps) {
  const queryClient = useQueryClient();

  const networkStatus = useNetworkStatus({
    onReconnect: useCallback(() => {
      console.log('[Network] Reconnected, invalidating queries...');
      queryClient.invalidateQueries();
    }, [queryClient]),
    onDisconnect: useCallback(() => {
      console.log('[Network] Disconnected.');
    }, []),
  });

  return <NetworkContext.Provider value={networkStatus}>{children}</NetworkContext.Provider>;
}

export default NetworkProvider;
